import React, { useState, useEffect } from "react";
import { Form, Input, Select, Button, Row, Col, DatePicker, Slider } from "antd";
import {
    SearchOutlined,
    ClearOutlined,
    FilterOutlined,
    UpOutlined,
    DownOutlined,
    PhoneOutlined,
    UserOutlined,
    ProjectOutlined,
    CheckCircleOutlined,
    IdcardOutlined,
    CalendarOutlined,
    AppstoreOutlined,
    WhatsAppOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

const { Option } = Select;
const { RangePicker } = DatePicker;

// Quick filter button labels with day offsets for date filters and status for call status filters
const QUICK_FILTERS = [
    { label: "Today", days: 0, type: "date" },
    { label: "Yesterday", days: 1, type: "date" },
    { label: "Last 3 Days", days: 3, type: "date" },
    { label: "Last 7 Days", days: 7, type: "date" },
    { label: "Last 30 Days", days: 30, type: "date" },
    { label: "Last 90 Days", days: 90, type: "date" },
    // { label: "Last 365 Days", days: 365, type: "date" },
    { label: "Answered", status: "Answered", type: "status" },
    { label: "Not Answered", status: "NotAnswered", type: "status" },
];

const hasValue = (v: any) => v !== undefined && v !== null && v !== "";

const WHATSAPP_FEEDBACK_AGENT = "SAHARA";

// Agent-specific quick filter pills (separate from date/status)
const AGENT_QUICK_FILTERS = [
    { label: "SAHARA", agent: "SAHARA" },
    { label: "SAMAIRA", agent: "SAMAIRA" },
];

interface OzonetelFiltersProps {
    onFilter: (values: any, activeQuick?: string | null) => void;
    onReset: () => void;
    loading: boolean;
    filterOptions: {
        Agent: string[];
        AgentStatus: string[];
        CampaignName: string[];
        Status: string[];
        Type: string[];
        ivr_feedback?: string[];
        whatsapp_feedback?: string[];
    };
    initialValues?: any;
    initialActiveQuick?: string | null;
}

// Ozonetel filter panel with single RangePicker and quick filter pills, matching Sahara design
const OzonetelFilters = ({ onFilter, onReset, loading, filterOptions, initialValues, initialActiveQuick }: OzonetelFiltersProps) => {
    const [form] = Form.useForm();
    const [visible, setVisible] = useState(true);
    const [activeQuick, setActiveQuick] = useState<string | null>(initialActiveQuick ?? "Today");
    const [clearDateFlag, setClearDateFlag] = useState(false);
    const [showDatePicker, setShowDatePicker] = useState(true);
    const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
    const selectedAgent = Form.useWatch("Agent", form);
    const showWhatsappFeedback =
        String(selectedAgent || "").toUpperCase() === WHATSAPP_FEEDBACK_AGENT;

    useEffect(() => {
        if (!showWhatsappFeedback && form.getFieldValue("whatsapp_feedback")) {
            form.setFieldsValue({ whatsapp_feedback: undefined });
        }
    }, [showWhatsappFeedback, form]);

    useEffect(() => {
        if (initialValues) {
            const values = { ...initialValues };
            if (values.E164 && values.E164.startsWith("+91")) {
                values.E164 = values.E164.substring(3);
            }
            // Reconstruct RangePicker value from stored start/end date strings
            if (values.start_date && values.end_date) {
                values.date_range = [
                    dayjs(values.start_date, "YYYY-MM-DD"),
                    dayjs(values.end_date, "YYYY-MM-DD"),
                ];
            } else {
                // No saved dates — default range picker to today
                values.date_range = [dayjs(), dayjs()];
            }
            form.setFieldsValue(values);
        }
    }, [initialValues, form]);

    const emitFilter = (values: any, label: string | null = null) => {
        const payload = { ...values };
        if (String(payload.Agent || "").toUpperCase() !== WHATSAPP_FEEDBACK_AGENT) {
            delete payload.whatsapp_feedback;
        }
        onFilter(payload, label);
    };

    // Apply quick filter by setting date range or status and immediately triggering filter
    const applyQuickFilter = (label: string, days?: number, status?: string, type?: string, callType?: string, agent?: string) => {
        setActiveQuick(label);
        setVisible(true);

        const current = form.getFieldsValue();
        const e164 = current.E164 && !current.E164.startsWith("+91") ? `+91${current.E164}` : current.E164;

        if (type === "agent" && agent) {
            // Set Agent filter and keep current date range
            form.setFieldsValue({ Agent: agent });
            const { date_range, ...restCurrent } = current;
            let start_date = restCurrent.start_date || "";
            let end_date = restCurrent.end_date || "";
            if (date_range && date_range.length === 2 && date_range[0] && date_range[1]) {
                start_date = date_range[0].format("YYYY-MM-DD") + "T00:00:00";
                end_date = date_range[1].format("YYYY-MM-DD") + "T23:59:59";
            }
            emitFilter({
                ...restCurrent,
                E164: e164 || "",
                Agent: agent,
                start_date,
                end_date,
            }, label);
        } else if (type === "status") {
            form.setFieldsValue({ Status: status, date_range: undefined });
            emitFilter({
                ...current,
                E164: e164 || "",
                Status: status || "",
                date_range: undefined,
                start_date: "",
                end_date: "",
            }, label);
        } else if (type === "calltype") {
            form.setFieldsValue({ Type: callType });
            const { date_range, ...restCurrent } = current;
            let start_date = restCurrent.start_date || "";
            let end_date = restCurrent.end_date || "";
            if (date_range && date_range.length === 2 && date_range[0] && date_range[1]) {
                start_date = date_range[0].format("YYYY-MM-DD") + "T00:00:00";
                end_date = date_range[1].format("YYYY-MM-DD") + "T23:59:59";
            }
            emitFilter({
                ...restCurrent,
                E164: e164 || "",
                Type: callType || "",
                start_date,
                end_date,
            }, label);
        } else {
            let startDay: dayjs.Dayjs;
            let endDay: dayjs.Dayjs;

            if (days === 0) {
                startDay = dayjs();
                endDay = dayjs();
            } else if (days === 1) {
                startDay = dayjs().subtract(1, "day");
                endDay = dayjs().subtract(1, "day");
            } else {
                startDay = dayjs().subtract(days || 0, "day");
                endDay = dayjs();
            }

            form.setFieldsValue({ date_range: [startDay, endDay] });
            emitFilter({
                ...current,
                E164: e164 || "",
                date_range: undefined,
                start_date: startDay.format("YYYY-MM-DD") + "T00:00:00",
                end_date: endDay.format("YYYY-MM-DD") + "T23:59:59",
            }, label);
        }
    };

    // Handle form submission, extracting date_range into start_date/end_date and clearing active quick
    const handleFinish = (values: any) => {
        setActiveQuick(null);
        const processed = { ...values };
        if (processed.E164 && !processed.E164.startsWith("+91")) {
            processed.E164 = `+91${processed.E164}`;
        }
        const hasUCID = !!(processed.UCID && processed.UCID.trim());
        const hasE164 = !!(processed.E164 && processed.E164.trim() && processed.E164.trim() !== "+91");
        if (hasUCID || hasE164) {
            processed.start_date = "";
            processed.end_date = "";
            form.setFieldsValue({ date_range: undefined });
        } else if (processed.date_range && processed.date_range.length === 2) {
            processed.start_date = processed.date_range[0].format("YYYY-MM-DD") + "T00:00:00";
            processed.end_date = processed.date_range[1].format("YYYY-MM-DD") + "T23:59:59";
        } else {
            processed.start_date = "";
            processed.end_date = "";
        }
        delete processed.date_range;

        // Build flat dot-notation analysis filter keys (e.g. "analysis.priority": "High")
        const {
            mf_priority, mf_issue_category,
            mf_customer_satisfaction, mf_customer_tone, mf_agent_tone, mf_agent_professionalism_score,
            mf_issue_resolved,
            mf_empathy_shown, mf_offered_next_steps, mf_knowledge_gap_detected,
            mf_call_closure_quality, mf_repeated_phrases_detected, mf_escalation_offered,
            mf_agent_prof_score_profiling,
            ...rest
        } = processed;

        const payload: any = { ...rest };
        if (hasValue(mf_priority)) payload['analysis.priority'] = mf_priority;
        if (hasValue(mf_issue_category)) payload['analysis.entities.issue_category'] = mf_issue_category;
        if (hasValue(mf_issue_resolved)) payload['analysis.call_metadata.issue_resolved'] = mf_issue_resolved;
        if (hasValue(mf_customer_satisfaction)) payload['analysis.sentiment_and_profiling.customer_satisfaction'] = mf_customer_satisfaction;
        if (hasValue(mf_customer_tone)) payload['analysis.sentiment_and_profiling.customer_tone'] = mf_customer_tone;
        if (hasValue(mf_agent_tone)) payload['analysis.sentiment_and_profiling.agent_tone'] = mf_agent_tone;
        if (hasValue(mf_agent_professionalism_score)) payload['analysis.sentiment_and_profiling.agent_professionalism_score'] = mf_agent_professionalism_score;
        if (hasValue(mf_empathy_shown)) payload['analysis.agent_profiling.empathy_shown'] = mf_empathy_shown;
        if (hasValue(mf_offered_next_steps)) payload['analysis.agent_profiling.offered_next_steps'] = mf_offered_next_steps;
        if (hasValue(mf_knowledge_gap_detected)) payload['analysis.agent_profiling.knowledge_gap_detected'] = mf_knowledge_gap_detected;
        if (hasValue(mf_call_closure_quality)) payload['analysis.agent_profiling.call_closure_quality'] = mf_call_closure_quality;
        if (hasValue(mf_repeated_phrases_detected)) payload['analysis.agent_profiling.repeated_phrases_detected'] = mf_repeated_phrases_detected;
        if (hasValue(mf_escalation_offered)) payload['analysis.agent_profiling.escalation_offered'] = mf_escalation_offered;
        if (hasValue(mf_agent_prof_score_profiling)) payload['analysis.agent_profiling.agent_professionalism_score'] = mf_agent_prof_score_profiling;

        emitFilter(payload, null);
    };

    // Handle reset action, clearing quick filter state
    const handleReset = () => {
        setActiveQuick(null);
        form.resetFields();
        onReset();
    };

    const handleSearchFieldChange = (fieldValue: string | undefined) => {
        if (fieldValue && fieldValue.trim() !== "") {
            form.setFieldsValue({ date_range: undefined });
            setActiveQuick(null);
            setClearDateFlag((prev) => !prev);
            setShowDatePicker(false);
        } else {
            setShowDatePicker(true);
        }
    };

    return (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden mb-6">
            {/* Quick filter pill buttons row */}
            <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-100 flex-wrap">
                <span className="text-xs text-gray-500 font-medium mr-1">Quick:</span>
                {QUICK_FILTERS.map(({ label, days, status, type }: any) => (
                    <button
                        key={label}
                        onClick={() => applyQuickFilter(label, days, status, type)}
                        className={`px-3 py-1 rounded-full text-xs font-medium border transition-all duration-150 cursor-pointer ${activeQuick === label
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-600 border-gray-200 hover:border-blue-400 hover:text-blue-600'
                            }`}
                    >
                        {label}
                    </button>
                ))}
                {/* Dynamic Type pills from API */}
                {(filterOptions.Type || []).map((callType: string) => {
                    const t = callType?.toLowerCase();
                    const activeStyle = t === 'ivr'
                        ? 'bg-purple-600 text-white border-purple-600'
                        : t === 'inbound'
                            ? 'bg-green-600 text-white border-green-600'
                            : 'bg-blue-600 text-white border-blue-600';
                    return (
                        <button
                            key={callType}
                            onClick={() => applyQuickFilter(callType, undefined, undefined, 'calltype', callType)}
                            className={`px-3 py-1 rounded-full text-xs font-medium border transition-all duration-150 cursor-pointer ${activeQuick === callType ? activeStyle : 'bg-white text-gray-600 border-gray-200 hover:border-blue-400 hover:text-blue-600'
                                }`}
                        >
                            {callType}
                        </button>
                    );
                })}
                {/* Agent quick filter pills */}
                {AGENT_QUICK_FILTERS.map(({ label, agent }) => (
                    <button
                        key={label}
                        onClick={() => applyQuickFilter(label, undefined, undefined, 'agent', undefined, agent)}
                        className={`px-3 py-1 rounded-full text-xs font-medium border transition-all duration-150 cursor-pointer ${activeQuick === label
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-600 border-gray-200 hover:border-blue-400 hover:text-blue-600'
                            }`}
                        title={`Filter by Agent = ${agent}`}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {/* Toggle header bar with filter label and expand/collapse button */}
            <div
                className="flex items-center justify-between px-5 py-3 cursor-pointer select-none hover:bg-gray-50 transition-colors"
                onClick={() => setVisible((v) => !v)}
            >
                <div className="flex items-center gap-2 text-gray-600 font-medium text-sm">
                    <FilterOutlined />
                    <span>Filters</span>
                </div>
                <UpOutlined
                    className="text-gray-400 text-xs transition-transform duration-300"
                    style={{ transform: visible ? "rotate(0deg)" : "rotate(180deg)" }}
                />
            </div>

            {/* Collapsible filter form panel */}
            {visible && (
                <Form
                    form={form}
                    onFinish={handleFinish}
                    layout="vertical"
                    className="px-5 pt-4 pb-3 border-t border-gray-100"
                    size="small"
                >
                    <Row gutter={[16, 4]}>
                        {/* UCID filter input */}
                        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                            <Form.Item name="UCID" label="UCID" style={{ marginBottom: 12 }}>
                                <Input
                                    placeholder="Search by UCID"
                                    prefix={<IdcardOutlined className="text-gray-400" />}
                                    allowClear
                                    onChange={(e) => handleSearchFieldChange(e.target.value)}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                            {/* Agent filter select */}
                            <Form.Item name="Agent" label="Agent" style={{ marginBottom: 12 }}>
                                <Select
                                    placeholder="Select Agent"
                                    className="w-full"
                                    showSearch
                                    allowClear
                                    optionFilterProp="children"
                                    suffixIcon={<UserOutlined className="text-gray-400" />}
                                >
                                    {filterOptions.Agent?.map((agent: string) => (
                                        <Option key={agent} value={agent}>{agent}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                            {/* Agent Status filter select */}
                            <Form.Item name="AgentStatus" label="Agent Status" style={{ marginBottom: 12 }}>
                                <Select
                                    placeholder="Select Agent Status"
                                    className="w-full"
                                    allowClear
                                >
                                    {filterOptions.AgentStatus?.map((status: string) => (
                                        <Option key={status} value={status}>{status}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                            {/* E164 filter input with +91 prefix */}
                            <Form.Item name="E164" label="E164" style={{ marginBottom: 12 }}>
                                <Input
                                    placeholder="Search by E164"
                                    addonBefore="+91"
                                    prefix={<PhoneOutlined className="text-gray-400" />}
                                    allowClear
                                    onChange={(e) => handleSearchFieldChange(e.target.value)}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                            {/* Campaign filter select */}
                            <Form.Item name="CampaignName" label="Campaign" style={{ marginBottom: 12 }}>
                                <Select
                                    placeholder="Select Campaign"
                                    className="w-full"
                                    showSearch
                                    allowClear
                                    optionFilterProp="children"
                                    suffixIcon={<ProjectOutlined className="text-gray-400" />}
                                >
                                    {filterOptions.CampaignName?.map((campaign: string) => (
                                        <Option key={campaign} value={campaign}>{campaign}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                            {/* Call Status filter select */}
                            <Form.Item name="Status" label="Call Status" style={{ marginBottom: 12 }}>
                                <Select
                                    placeholder="Select Call Status"
                                    className="w-full"
                                    allowClear
                                    suffixIcon={<CheckCircleOutlined className="text-gray-400" />}
                                >
                                    {filterOptions.Status?.map((status: string) => (
                                        <Option key={status} value={status}>{status}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                            <Form.Item name="Type" label="Type" style={{ marginBottom: 12 }}>
                                <Select
                                    placeholder="Select Type"
                                    className="w-full"
                                    allowClear
                                >
                                    {(filterOptions.Type || []).map((t: string) => (
                                        <Option key={t} value={t}>{t}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={6} xl={6}>
                            {/* Date range picker for start and end date in a single control */}
                            {showDatePicker && (
                                <Form.Item name="date_range" label="Date Range" style={{ marginBottom: 12 }}>
                                    <RangePicker
                                        key={clearDateFlag ? 'clear-1' : 'clear-0'} // toggles on clear to reset internal state
                                        className="w-full"
                                        suffixIcon={<CalendarOutlined className="text-gray-400" />}
                                        disabledDate={(d) => d && d.isAfter(dayjs(), "day")}
                                        onChange={() => setActiveQuick(null)}
                                    />
                                </Form.Item>
                            )}
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                            <Form.Item name="ivr_feedback" label="IVR Feedback" style={{ marginBottom: 12 }}>
                                <Select
                                    placeholder="Select IVR Feedback"
                                    className="w-full"
                                    allowClear
                                >
                                    {(filterOptions.ivr_feedback || []).map((fb: string) => (
                                        <Option key={fb} value={fb}>{fb.replace('_', ' ')}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        {showWhatsappFeedback && (
                            <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                                <Form.Item
                                    name="whatsapp_feedback"
                                    label="WhatsApp Feedback"
                                    style={{ marginBottom: 12 }}
                                >
                                    <Select
                                        placeholder="Select WhatsApp Feedback"
                                        className="w-full"
                                        allowClear
                                        suffixIcon={<WhatsAppOutlined className="text-gray-400" />}
                                    >
                                        {(filterOptions.whatsapp_feedback || []).map((fb: string) => (
                                            <Option key={fb} value={fb}>{fb}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}
                    </Row>

                    {/* More Filters toggle */}
                    <div
                        className="flex items-center gap-2 mt-1 mb-2 cursor-pointer select-none w-fit"
                        onClick={() => setMoreFiltersOpen(v => !v)}
                        style={{ userSelect: 'none' }}
                    >
                        <span
                            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full border transition-all duration-150"
                            style={{
                                background: moreFiltersOpen ? '#eff6ff' : '#f9fafb',
                                color: moreFiltersOpen ? '#2563eb' : '#6b7280',
                                border: moreFiltersOpen ? '1px solid #bfdbfe' : '1px solid #e5e7eb',
                            }}
                        >
                            <AppstoreOutlined style={{ fontSize: 11 }} />
                            More Filters
                            {moreFiltersOpen
                                ? <UpOutlined style={{ fontSize: 9 }} />
                                : <DownOutlined style={{ fontSize: 9 }} />}
                        </span>
                    </div>
                    {/* Collapsible misc filters panel */}
                    {moreFiltersOpen && (
                        <div
                            className="rounded-lg border border-blue-100 bg-blue-50/30 px-3 pt-2 pb-1 mb-2"
                            style={{ animation: 'mfSlideDown 0.18s ease' }}
                        >
                            <style>{`
                                @keyframes mfSlideDown {
                                    from { opacity: 0; transform: translateY(-6px); }
                                    to   { opacity: 1; transform: translateY(0); }
                                }
                            `}</style>

                            {/* Row 1: Call Analysis + Sentiment */}
                            <div className="text-[9px] font-bold uppercase tracking-widest text-blue-400 mb-1 flex items-center gap-1">
                                <span className="inline-block w-2 h-px bg-blue-300" />Call Analysis &amp; Sentiment
                            </div>
                            <Row gutter={[8, 2]}>
                                <Col xs={12} sm={8} md={6} lg={4} xl={3}>
                                    <Form.Item name="mf_priority" label="Priority" style={{ marginBottom: 6 }}>
                                        <Select placeholder="Priority" allowClear size="small">
                                            {['High', 'Medium', 'Low'].map(v => <Option key={v} value={v}>{v}</Option>)}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={12} sm={8} md={6} lg={5} xl={4}>
                                    <Form.Item name="mf_issue_category" label="Issue Category" style={{ marginBottom: 6 }}>
                                        <Select placeholder="Category" allowClear size="small">
                                            {['Delivery Delay', 'Damaged Item', 'Refund', 'Pre-purchase Query', 'Other'].map(v => <Option key={v} value={v}>{v}</Option>)}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={12} sm={8} md={6} lg={4} xl={3}>
                                    <Form.Item name="mf_issue_resolved" label="Issue Resolved" style={{ marginBottom: 6 }}>
                                        <Select placeholder="Resolved?" allowClear size="small">
                                            {['Yes', 'No', 'Partially'].map(v => <Option key={v} value={v}>{v}</Option>)}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={12} sm={8} md={6} lg={4} xl={3}>
                                    <Form.Item name="mf_customer_satisfaction" label="Cust. Satisfaction" style={{ marginBottom: 6 }}>
                                        <Select placeholder="Satisfaction" allowClear size="small">
                                            {['Very Dissatisfied', 'Dissatisfied', 'Neutral', 'Satisfied', 'Very Satisfied'].map(v => <Option key={v} value={v}>{v}</Option>)}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={12} sm={8} md={6} lg={4} xl={3}>
                                    <Form.Item name="mf_customer_tone" label="Cust. Tone" style={{ marginBottom: 6 }}>
                                        <Select placeholder="Tone" allowClear size="small">
                                            {['Calm', 'Angry', 'Confused', 'Polite', 'Anxious'].map(v => <Option key={v} value={v}>{v}</Option>)}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={12} sm={8} md={6} lg={4} xl={3}>
                                    <Form.Item name="mf_agent_tone" label="Agent Tone" style={{ marginBottom: 6 }}>
                                        <Select placeholder="Tone" allowClear size="small">
                                            {['Empathetic', 'Professional', 'Rushed', 'Indifferent'].map(v => <Option key={v} value={v}>{v}</Option>)}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={12} sm={8} md={6} lg={4} xl={3}>
                                    <Form.Item name="mf_agent_professionalism_score" label="Agent Prof. Score" style={{ marginBottom: 6 }}>
                                        <Select placeholder="1-5" allowClear size="small">
                                            {[1, 2, 3, 4, 5].map(v => <Option key={v} value={v}>{v}</Option>)}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>

                            {/* Row 2: Agent Profiling */}
                            <div className="text-[9px] font-bold uppercase tracking-widest text-emerald-500 mb-1 mt-1 flex items-center gap-1">
                                <span className="inline-block w-2 h-px bg-emerald-300" />Agent Profiling
                            </div>
                            <Row gutter={[8, 2]}>
                                <Col xs={12} sm={8} md={6} lg={4} xl={3}>
                                    <Form.Item name="mf_empathy_shown" label="Empathy" style={{ marginBottom: 6 }}>
                                        <Select placeholder="Y/N" allowClear size="small">
                                            <Option value="Yes">Yes</Option><Option value="No">No</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={12} sm={8} md={6} lg={4} xl={3}>
                                    <Form.Item name="mf_offered_next_steps" label="Next Steps" style={{ marginBottom: 6 }}>
                                        <Select placeholder="Y/N" allowClear size="small">
                                            <Option value="Yes">Yes</Option><Option value="No">No</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={12} sm={8} md={6} lg={4} xl={3}>
                                    <Form.Item name="mf_knowledge_gap_detected" label="Knowledge Gap" style={{ marginBottom: 6 }}>
                                        <Select placeholder="Y/N" allowClear size="small">
                                            <Option value="Yes">Yes</Option><Option value="No">No</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={12} sm={8} md={6} lg={4} xl={3}>
                                    <Form.Item name="mf_repeated_phrases_detected" label="Repeated Phrases" style={{ marginBottom: 6 }}>
                                        <Select placeholder="Y/N" allowClear size="small">
                                            <Option value="Yes">Yes</Option><Option value="No">No</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={12} sm={8} md={6} lg={4} xl={3}>
                                    <Form.Item name="mf_escalation_offered" label="Escalation" style={{ marginBottom: 6 }}>
                                        <Select placeholder="Y/N" allowClear size="small">
                                            <Option value="Yes">Yes</Option><Option value="No">No</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={12} sm={8} md={6} lg={4} xl={3}>
                                    <Form.Item name="mf_call_closure_quality" label="Closure Quality" style={{ marginBottom: 6 }}>
                                        <Select placeholder="1-5" allowClear size="small">
                                            {[1, 2, 3, 4, 5].map(v => <Option key={v} value={v}>{v}</Option>)}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={12} sm={8} md={6} lg={4} xl={3}>
                                    <Form.Item name="mf_agent_prof_score_profiling" label="Prof. Score" style={{ marginBottom: 6 }}>
                                        <Select placeholder="1-5" allowClear size="small">
                                            {[1, 2, 3, 4, 5].map(v => <Option key={v} value={v}>{v}</Option>)}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </div>
                    )}

                    {/* Action buttons aligned to the right */}
                    <div className="flex justify-end gap-2 pt-1 pb-1 border-t border-gray-50 mt-2">
                        <Button size="small" icon={<ClearOutlined />} onClick={handleReset}>
                            Reset
                        </Button>
                        <Button
                            size="small"
                            type="primary"
                            htmlType="submit"
                            icon={<SearchOutlined />}
                            loading={loading}
                            className="bg-blue-600"
                        >
                            Filter
                        </Button>
                    </div>
                </Form>
            )
            }
        </div >
    );
};

export default OzonetelFilters;
